import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/spotlight/styles.css';
import 'react-datasheet-grid/dist/style.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/dosis/400.css';
import '@fontsource/dosis/500.css';
import '@fontsource/dosis/600.css';
import '@fontsource/dosis/700.css';

import './styles/global.css';
import { theme } from './styles/theme';
import App from './App';
import { AuthProvider } from './lib/auth/AuthProvider';
import { AppColorSchemeProvider, useAppColorScheme } from './lib/colorScheme';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { initSentry } from './lib/sentry';

initSentry();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

function InnerApp() {
  const { resolvedScheme } = useAppColorScheme();

  return (
    <MantineProvider theme={theme} defaultColorScheme="dark" forceColorScheme={resolvedScheme}>
      <Notifications position="top-right" autoClose={3500} />
      <QueryClientProvider client={queryClient}>
        <AppErrorBoundary>
          <AuthProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </AuthProvider>
        </AppErrorBoundary>
      </QueryClientProvider>
    </MantineProvider>
  );
}

function Root() {
  return (
    <AppColorSchemeProvider>
      <InnerApp />
    </AppColorSchemeProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>,
);
